import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, HostBinding, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApolloError } from '@apollo/client/core';
import { Store, createSelector } from '@ngrx/store';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { finalize, map } from 'rxjs/operators';

import { FreyaCommonModule } from 'src/app/freya-common/freya-common.module';
import { quickJsActions } from 'src/app/quickjs/quickjs.actions';
import { quickjsFeature } from 'src/app/quickjs/quickjs.feature';
import { FreyaNotificationsService } from 'src/app/services/freya-notifications.service';
import { JOB_EXECUTION_THIRD_PARTY, jobExecutionZeroesFpPayout, normalizeJobExecutionType } from 'src/app/shared/job-execution-type';
import { brandingFeature } from 'src/app/state/branding.store';
import { SetCrewDocFlagsGQL } from 'src/generated/graphql.generated';
import { SubSink } from 'subsink';


import { zonesFeature } from '../../../../core/zones/zones.reducer';
import { selectCanEditSharedCrewPay } from '../../../../shared/unsaved-changes-banner/unsaved-changes-banner.selectors';
import { selectJob } from '../../../job-tool.reducer';

import {
    assertCrewDocFlagsSaved,
    contractRuntimeEnabledKey,
    contractRuntimeUpdatedAtKey,
    isDocFlagChecked,
    nowSeconds,
    payToFpRuntimeEnabledKey,
    payToFpRuntimeUpdatedAtKey,
    readUserDocFlag,
    UserDocFlag,
    USER_DOC_FLAG_LABELS,
} from './crew-doc-flags';
import { CREW_PAY_CALC_KEYS, CREW_PAY_CREW_LEAD_CALC_KEYS, CrewPayColumnKey, PAYABLE_CREW_ROLES } from './crew-pay.constants';

// Crew pay amounts, the payment-summary table (Table 2), and the monetary alerts are
// only shown to users whose active zone is the OTR interstate zone. Everyone else sees
// Table 1's checkbox assignments (still editable), without any dollar figures.
const INTERSTATE_ZONE_ID = 'ymm-interstate-usa';

interface CrewPayColumnDef {
    key: CrewPayColumnKey;
    label: string;
    getExpectedCount: (cs: Record<string, any>, crewCount: number) => number;
}

const TIPS_KEY = 'crewPay__tips';

interface CrewPayAlert {
    key: string;
    label: string;
    detail: string;
    severity?: 'warn' | 'info';
}

// Table 1 columns (checkboxes). Bonus lives in Table 2.
const CREW_PAY_COLUMNS: CrewPayColumnDef[] = [
    {
        key: 'crewLead',
        label: 'Crew Lead',
        getExpectedCount: (cs) => Number(cs[CREW_PAY_CALC_KEYS.crewLead] ?? 0) > 0 ? 1 : 0,
    },
    {
        key: 'flatRate',
        label: 'Reg. Crew',
        getExpectedCount: (cs, crewCount) => Math.max(0, crewCount - 1),
    },
    {
        key: 'packing',
        label: 'Packing',
        getExpectedCount: (cs) => Number(cs['packingCrewSize'] ?? 0),
    },
    {
        key: 'unpacking',
        label: 'Unpacking',
        getExpectedCount: (cs) => Number(cs['unpackingCrewSize'] ?? 0),
    },
    {
        key: 'load',
        label: 'Load',
        getExpectedCount: (cs) => Number(cs['loadCrewSize'] ?? 0),
    },
    {
        key: 'unload',
        label: 'Unload',
        getExpectedCount: (cs) => Number(cs['unloadCrewSize'] ?? 0),
    },
];

@Component({
    selector: 'app-crew-pay-table',
    standalone: true,
    imports: [CommonModule, FormsModule, FreyaCommonModule, CurrencyPipe],
    templateUrl: './crew-pay-table.component.html',
    styleUrl: './crew-pay-table.component.scss',
})
export class CrewPayTableComponent implements OnDestroy {

    /**
     * Requirement: `#6951` - lifts this table above the shared-job overlay for those allowed to
     * tick its boxes (see the component styles). Cannot use `| async` in host bindings, so the
     * value is subscribed into this field, as `JobComponent` does.
     */
    @HostBinding('class.shared-zone-editable') sharedZoneEditable = false;

    private readonly subs = new SubSink();


    private readonly baseViewModel$ = this.store.select(createSelector(
        selectJob,
        quickjsFeature.selectCalculatedState,
        quickjsFeature.selectUserInputs,
        brandingFeature.selectCurrency,
        zonesFeature.selectCurrentAppZone,
        (job, cs, userInputs, currency, currentZone) => {
            const crewMap = new Map<string, { id: string; name: string; metadata: Record<string, unknown> | null | undefined }>();
            for (const event of job?.events ?? []) {
                for (const attendee of event.attendees ?? []) {
                    if (!attendee?.user?.id) continue;
                    if (!PAYABLE_CREW_ROLES.has(attendee.role)) continue;
                    if (!crewMap.has(attendee.user.id)) {
                        crewMap.set(attendee.user.id, {
                            id: attendee.user.id,
                            name: [attendee.user.givenName, attendee.user.familyName]
                                .filter(Boolean).join(' ') || attendee.user.id,
                            // Per-crew-member document flags (ACH/W-9) live on the User node's
                            // metadata (see crew-doc-flags). May be absent on legacy crew → unchecked.
                            metadata: attendee.user.metadata,
                        });
                    }
                }
            }
            const crew = Array.from(crewMap.values());

            // Tips distribution: collect per-user overrides first, then compute default share.
            const tipsTotalRaw = userInputs[TIPS_KEY];
            const tipsTotal: number | null = tipsTotalRaw != null ? Number(tipsTotalRaw) : null;
            const tipsTotalAmount = tipsTotal ?? 0;

            const tipOverrideByMemberId = new Map<string, number | null>(
                crew.map(m => {
                    const raw = userInputs[`crewPay__${m.id}__tip`];
                    return [m.id, raw != null ? Number(raw) : null];
                })
            );
            const sumOfTipOverrides = Array.from(tipOverrideByMemberId.values())
                .reduce((s, v) => s + (v ?? 0), 0);
            const overriddenTipCount = Array.from(tipOverrideByMemberId.values())
                .filter(v => v !== null).length;
            const nonOverriddenTipCount = crew.length - overriddenTipCount;
            const defaultTip = nonOverriddenTipCount > 0
                ? Math.max(0, (tipsTotalAmount - sumOfTipOverrides) / nonOverriddenTipCount)
                : 0;

            const activeCols = CREW_PAY_COLUMNS;

            // Requirement: `#6620` - the execution type decides whether this job funds FP labor at all.
            const jobExecutionType = normalizeJobExecutionType(cs['jobExecutionType']);
            const isThirdParty = jobExecutionType === JOB_EXECUTION_THIRD_PARTY;
            const zeroesFpPayout = jobExecutionZeroesFpPayout(jobExecutionType);

            const rows = crew.map(member => {
                // Requirement: `#7194` - mirrors the calculator's crew-pay loop: the member ticked as
                // Crew Lead is paid the lead's share on the columns that have one.
                const crewLeadKey = `crewPay__${member.id}__crewLead__enabled`;
                const isCrewLead = userInputs[crewLeadKey] === true || userInputs[crewLeadKey] === 'true';

                // Table 1: checkbox columns (no bonus)
                const columns = activeCols.map(col => {
                    const enabledKey = `crewPay__${member.id}__${col.key}__enabled`;
                    const enabled = userInputs[enabledKey] === true || userInputs[enabledKey] === 'true';
                    const crewLeadCalcKey = CREW_PAY_CREW_LEAD_CALC_KEYS[col.key];
                    const rateKey = isCrewLead && crewLeadCalcKey && cs[crewLeadCalcKey] != null
                        ? crewLeadCalcKey
                        : CREW_PAY_CALC_KEYS[col.key];
                    const defaultValue = Number(cs[rateKey] ?? 0);
                    const disabled = col.getExpectedCount(cs, crew.length) === 0;
                    return { key: col.key, enabledKey, enabled, defaultValue, disabled };
                });

                // Requirement: `#6538` - "Pay Labor to FP": the Franchise Partner pays this member
                // locally, so their labor money is forwarded to the FP as a lump sum instead of
                // being paid here. Mirrors the calculator (section 11d): the phase assignments
                // (`__enabled`) stay intact, only the amounts are zeroed. Tips are NOT forwarded —
                // they come from the job-level tips pool, not from the labor buckets.
                //
                // Requirement: `#6620` - neutralised when the job type zeroes the FP payout, matching
                // the calculator exactly (section 11d). The flag zeroes the member's amounts on the
                // assumption the money reaches them via the FP; with no FP on the job it would zero
                // them and forward nothing, so their labor would appear to vanish. Reading the raw
                // flag here instead would make this table disagree with the calculator's own figures.
                const payToFpKey = payToFpRuntimeEnabledKey(member.id);
                const payToFp = !zeroesFpPayout && isDocFlagChecked(userInputs[payToFpKey]);

                // Requirement: `#6620` - this member's labor is not paid by OTR on this job: either
                // it is forwarded to the Franchise Partner (`#6538`), or an outside agency is being
                // paid for the whole job instead. Their pay is zeroed on the same terms in both
                // cases, mirroring the calculator's own crew-pay loop — without it this table would
                // show a member as owed money the job does not fund. Tips and Paid are carved out
                // below, for the same reasons the forwarding override carves them out.
                const laborPaidElsewhere = payToFp || isThirdParty;

                // Fees for service = sum of enabled Table 1 columns (no bonus, no tips).
                const computedFees = columns.reduce((sum, c) => sum + (c.enabled ? c.defaultValue : 0), 0);

                const feesOverrideKey = `crewPay__${member.id}__fees`;
                const feesOverrideRaw = userInputs[feesOverrideKey];
                const feesOverride: number | null = feesOverrideRaw != null ? Number(feesOverrideRaw) : null;
                const displayFees = laborPaidElsewhere ? 0 : (feesOverride ?? computedFees);

                // Bonus (Table 2, checkbox)
                const bonusEnabledKey = `crewPay__${member.id}__bonus__enabled`;
                const bonusEnabled = userInputs[bonusEnabledKey] === true || userInputs[bonusEnabledKey] === 'true';
                const bonusDefaultValue = Number(cs[CREW_PAY_CALC_KEYS['bonus']] ?? 0);
                const bonusDisabled = Number(cs['drivingCrewSize'] ?? 0) === 0;
                const bonusCol = {
                    enabledKey: bonusEnabledKey,
                    enabled: bonusEnabled,
                    defaultValue: bonusDefaultValue,
                    disabled: bonusDisabled
                };
                const computedBonus = laborPaidElsewhere || !bonusEnabled || bonusDisabled ? 0 : bonusDefaultValue;

                // Tips
                const tipOverrideKey = `crewPay__${member.id}__tip`;
                const tipOverride = tipOverrideByMemberId.get(member.id) ?? null;
                const computedTip = tipsTotalAmount > 0 && tipOverride !== null ? tipOverride : defaultTip;

                // Total (read-only) = fees + bonus + tips
                const displayTotal = displayFees + computedBonus + computedTip;

                // Paid / Unpaid. Read the same way for everyone, including members whose labor is
                // paid elsewhere — this deliberately mirrors the calculator's crew-pay loop rather
                // than forcing 0.
                //
                // Forcing it hid a real payment: this value is bound straight to the editable Paid
                // field, so a member paid $400 before the toggle was flipped showed Paid 0 on a
                // screen used to decide payments, while the calculator and the Labor dashboard both
                // still reported the 400. Their tip is also still owed and still needs settling.
                //
                // A payment made before the switch can now exceed the (tips-only) total. That is a
                // real discrepancy — money went to someone whose labor is now being paid for twice —
                // so it drives `unpaid` negative and raises the usual overpay alert, which is
                // exactly what the calculator says should happen.
                const paidKey = `crewPay__${member.id}__paid`;
                const paidRaw = userInputs[paidKey];
                const paidAmount: number | null = paidRaw != null ? Number(paidRaw) : null;
                const unpaid = displayTotal - (paidAmount ?? 0);

                // Document-compliance flags (Table 3). Contract is per (job × crew member),
                // stored in the calculator crewPay inputs; ACH/W-9 are per crew member, read
                // from the User node's metadata.
                const contractEnabled = isDocFlagChecked(userInputs[contractRuntimeEnabledKey(member.id)]);
                const achInfo = readUserDocFlag(member.metadata, 'ach');
                const w9 = readUserDocFlag(member.metadata, 'w9');

                return {
                    ...member,
                    columns,
                    payToFpKey,
                    payToFp,
                    // Zeroed pay and locked controls are one condition: the checkboxes size an
                    // amount nobody is paying, and a per-man rate beside them would claim pay the
                    // member is not receiving. The saved `__enabled` values are untouched, so
                    // switching back restores everything.
                    laborPaidElsewhere,
                    contractEnabled,
                    achInfo,
                    w9,
                    computedFees,
                    feesOverrideKey,
                    feesOverride,
                    feesHasOverride: feesOverride !== null,
                    displayFees,
                    bonusCol,
                    computedBonus,
                    tipOverrideKey,
                    tipOverride,
                    tipHasOverride: tipOverride !== null,
                    computedTip,
                    displayTotal,
                    paidKey,
                    paidAmount,
                    // Whether the USER entered a paid amount. Now simply `paidAmount !== null`: it
                    // needed the raw input only while `paidAmount` was forced to 0 for forwarded
                    // members, which is not null and so claimed an override on every such row.
                    paidHasOverride: paidAmount !== null,
                    unpaid,
                    unpaidIsNegative: unpaid < -0.001,
                };
            });

            const columnSummaries = activeCols.map(col => {
                const expectedCount = col.getExpectedCount(cs, crew.length);
                const assignedCount = rows.reduce((n, row) => {
                    const colState = row.columns.find(c => c.key === col.key);
                    return n + (colState?.enabled && !colState?.disabled ? 1 : 0);
                }, 0);
                // Requirement: `#6620` - a Third-Party job is expected to have no crew of its own, so
                // every column would read "under" and colour its header red. That is not a discrepancy
                // to fix, so the counts are reported as satisfied and the single execution-type notice
                // below carries the explanation instead.
                const status: 'ok' | 'over' | 'under' =
                    isThirdParty ? 'ok' :
                    assignedCount > expectedCount ? 'over' :
                    assignedCount < expectedCount ? 'under' :
                    'ok';
                return { key: col.key, label: col.label, status, disabled: expectedCount === 0 };
            });

            const overCountAlerts = columnSummaries
                .filter(c => c.status === 'over')
                .map(c => ({
                    key: `over__${c.key}`,
                    label: c.label,
                    detail: 'Total assigned exceeds the expected crew size for this column.',
                }));

            const underCountAlerts = columnSummaries
                .filter(c => c.status === 'under')
                .map(c => ({
                    key: `under__${c.key}`,
                    label: c.label,
                    detail: 'Total assigned is below the expected crew size for this column.',
                }));

            const doubleAssignmentAlerts = rows
                .filter(row =>
                    row.columns.find(c => c.key === 'crewLead')?.enabled &&
                    row.columns.find(c => c.key === 'flatRate')?.enabled
                )
                .map(row => ({
                    key: `double__${row.id}`,
                    label: row.name,
                    detail: 'This crew member is assigned as both Crew Lead and Reg. Crew.',
                }));

            const overpayAlerts = rows
                .filter(row => row.unpaidIsNegative)
                .map(row => ({
                    key: `overpay__${row.id}`,
                    label: row.name,
                    detail: 'Paid amount exceeds the total for this crew member.',
                }));

            const tipOverflowAlerts = tipsTotalAmount > 0 && nonOverriddenTipCount > 0 && sumOfTipOverrides > tipsTotalAmount
                ? [{
                    key: 'tip_overflow',
                    label: 'Tips',
                    detail: `Individual tip overrides ($${sumOfTipOverrides.toFixed(2)}) exceed the total tips`
                        + ` budget ($${tipsTotalAmount.toFixed(2)}). Non-overridden crew members will receive $0.`,
                }]
                : [];

            const tableTotals = {
                fees:   rows.reduce((sum, r) => sum + r.displayFees, 0),
                bonus:  rows.reduce((sum, r) => sum + r.computedBonus, 0),
                tips:   rows.reduce((sum, r) => sum + r.computedTip, 0),
                total:  rows.reduce((sum, r) => sum + r.displayTotal, 0),
                paid:   rows.reduce((sum, r) => sum + (r.paidAmount ?? 0), 0),
                unpaid: rows.reduce((sum, r) => sum + r.unpaid, 0),
            };

            const isInterstate = currentZone?.id === INTERSTATE_ZONE_ID;

            // Requirement: `#6620` - explains the locked table. NOT gated on the session zone: unlike
            // the amounts and the execution-type dropdown, Table 1's checkboxes are visible to every
            // zone, so a franchise user sees the lock and needs the reason for it too.
            const thirdPartyAlerts: CrewPayAlert[] = isThirdParty
                ? [{
                    key: 'third_party_execution',
                    label: 'Third-party job',
                    detail: 'An outside agency runs this move, so the job funds no FP labor and these'
                        + ' assignments cannot be edited. The saved values are kept, so changing the'
                        + ' execution type back restores them.',
                    severity: 'info',
                }]
                : [];

            // Non-interstate users don't see amounts or Table 2, so drop the two monetary
            // alerts (overpay, tip overflow — the latter prints dollar figures). The
            // crew-count/role alerts stay, since Table 1 remains visible and editable.
            //
            // Requirement: `#6620` - on a Third-Party job the crew-count and double-assignment alerts
            // are all spurious (the job is not meant to have crew at all), so they give way to the
            // single notice above. The monetary alerts stay: a recorded payment that now exceeds the
            // total is still a real problem that needs settling.
            const countAlerts = isThirdParty
                ? []
                : [...overCountAlerts, ...underCountAlerts, ...doubleAssignmentAlerts];

            const alerts: CrewPayAlert[] = isInterstate
                ? [...thirdPartyAlerts, ...countAlerts, ...overpayAlerts, ...tipOverflowAlerts]
                : [...thirdPartyAlerts, ...countAlerts];

            return {
                // Requirement: `#7001` - ACH/W-9 writes are authorized through this job.
                jobId: job?.id,
                rows,
                activeCols,
                columnSummaries,
                alerts,
                currency,
                hasCrew: crew.length > 0,
                tipsDisabled: tipsTotalAmount === 0,
                tableTotals,
                isInterstate,
                isThirdParty,
                // Requirement: `#6620` - the forwarding toggle itself is meaningless with no FP on the
                // job. Disabled rather than hidden, so an existing flag stays visible and reversible.
                payToFpLocked: zeroesFpPayout,
            };
        }
    ));

    // Optimistic overrides for the per-user doc flags (ACH/W-9), keyed by crew member id. The
    // base view model reads these off the loaded job's attendee metadata, which stays stale
    // until the job is refetched; overrides keep a toggled value visible immediately and are
    // reverted if the setCrewDocFlags write fails.
    private readonly docFlagOverrides$ = new BehaviorSubject<Record<string, { achInfo?: boolean; w9?: boolean }>>({});

    // In-flight per-user doc-flag writes, keyed by crew member id, so each row can show an
    // inline saving spinner on its ACH/W-9 checkbox while setCrewDocFlags runs.
    private readonly docFlagSaving$ = new BehaviorSubject<Record<string, { ach?: boolean; w9?: boolean }>>({});

    readonly viewModel$ = combineLatest([ this.baseViewModel$, this.docFlagOverrides$, this.docFlagSaving$ ]).pipe(
        map(([ vm, overrides, saving ]) => ({
            ...vm,
            rows: vm.rows.map((row) => {
                const override = overrides[row.id];
                const save = saving[row.id];
                return {
                    ...row,
                    achInfo: override?.achInfo ?? row.achInfo,
                    w9: override?.w9 ?? row.w9,
                    achSaving: !!save?.ach,
                    w9Saving: !!save?.w9,
                };
            }),
        })),
    );

    private readonly pendingFees: Record<string, number | null | undefined> = {};
    private readonly pendingTips: Record<string, number | null | undefined> = {};
    private readonly pendingPaid: Record<string, number | null | undefined> = {};

    protected readonly quickJsActions = quickJsActions;

    constructor(
        protected readonly store: Store,
        private readonly setCrewDocFlagsGQL: SetCrewDocFlagsGQL,
        private readonly notify: FreyaNotificationsService,
    ) {
        this.subs.sink = this.store.select(selectCanEditSharedCrewPay)
            // Host bindings cannot use the async pipe (see JobComponent).
            // eslint-disable-next-line @ngrx/no-store-subscription
            .subscribe((editable) => this.sharedZoneEditable = editable);
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    // ── Table 1: "Pay Labor to FP" override (#6538) ───────────────────────────

    // Per (job × crew member): zeroes this member's crew pay and forwards the money to the
    // Franchise Partner as a lump sum (calculator section 11d). Persisted on the normal job Save
    // like every other crewPay input; flag + updatedAt are one change, so one dispatch.
    togglePayToFp(memberId: string, checked: boolean): void {
        this.store.dispatch(quickJsActions.inputsChanged({
            inputs: [
                { key: payToFpRuntimeEnabledKey(memberId), value: checked },
                { key: payToFpRuntimeUpdatedAtKey(memberId), value: nowSeconds() },
            ],
        }));
    }

    // ── Table 3: document-compliance flags ────────────────────────────────────

    // Contract For This Job (per job × crew member): a manual crewPay input, persisted on the
    // normal job Save like every other crewPay checkbox. The flag and its updatedAt companion
    // are one conceptual change, so set them in a single dispatch.
    toggleContract(memberId: string, checked: boolean): void {
        this.store.dispatch(quickJsActions.inputsChanged({
            inputs: [
                { key: contractRuntimeEnabledKey(memberId), value: checked },
                { key: contractRuntimeUpdatedAtKey(memberId), value: nowSeconds() },
            ],
        }));
    }

    // ACH Info / W-9 (per crew member, global): written straight to the User via setCrewDocFlags,
    // independent of the job Save. Since there's no banner/Save step, feedback is an inline saving
    // spinner while the write runs plus a success/error toast. Optimistically override the row,
    // then revert the override if the mutation fails.
    //
    // Requirement: `#7001` - the write is authorized through this job, so it works for crew whose
    // own zone is outside the current one (the old editProfile path silently updated nobody for
    // them and still toasted "Saved"). Success is only reported once the server confirms the write.
    toggleUserFlag(jobId: string, memberId: string, memberName: string, flag: UserDocFlag, checked: boolean): void {
        const field: 'achInfo' | 'w9' = flag === 'ach' ? 'achInfo' : 'w9';
        this.setDocFlagOverride(memberId, field, checked);
        this.setDocFlagSaving(memberId, flag, true);

        this.setCrewDocFlagsGQL.mutate({
            jobId,
            userId: memberId,
            // Only the toggled flag is sent; the other stays as stored.
            achInfo: flag === 'ach' ? checked : undefined,
            w9: flag === 'w9' ? checked : undefined,
        }).pipe(
            map(({ data }) => assertCrewDocFlagsSaved(data?.setCrewDocFlags)),
            finalize(() => this.setDocFlagSaving(memberId, flag, false)),
        ).subscribe({
            next: () => {
                this.notify.success('Saved', `${USER_DOC_FLAG_LABELS[flag]} updated for ${memberName}`);
            },
            error: (error: ApolloError) => {
                this.setDocFlagOverride(memberId, field, !checked);
                this.notify.apolloError(`Failed to update ${USER_DOC_FLAG_LABELS[flag]} for ${memberName}`, error);
            },
        });
    }

    private setDocFlagOverride(memberId: string, field: 'achInfo' | 'w9', value: boolean): void {
        const current = this.docFlagOverrides$.value;
        this.docFlagOverrides$.next({
            ...current,
            [memberId]: { ...current[memberId], [field]: value },
        });
    }

    private setDocFlagSaving(memberId: string, flag: UserDocFlag, value: boolean): void {
        const current = this.docFlagSaving$.value;
        this.docFlagSaving$.next({
            ...current,
            [memberId]: { ...current[memberId], [flag]: value },
        });
    }

    // ── Per-row Fees for Service ──────────────────────────────────────────────

    onFeesInput(key: string, value: number | null): void {
        this.pendingFees[key] = value;
    }

    commitFees(key: string): void {
        if (!(key in this.pendingFees)) { return; }
        const value = this.pendingFees[key];
        delete this.pendingFees[key];
        if (value == null) {
            this.store.dispatch(quickJsActions.inputReset({ key }));
        } else {
            this.store.dispatch(quickJsActions.inputChanged({ key, value }));
        }
    }

    resetFees(feesOverrideKey: string): void {
        delete this.pendingFees[feesOverrideKey];
        this.store.dispatch(quickJsActions.inputReset({ key: feesOverrideKey }));
    }

    // ── Per-row Tip ──────────────────────────────────────────────────────────

    onTipInput(key: string, value: number | null): void {
        this.pendingTips[key] = value;
    }

    commitTip(key: string): void {
        if (!(key in this.pendingTips)) { return; }
        const value = this.pendingTips[key];
        delete this.pendingTips[key];
        if (value == null) {
            this.store.dispatch(quickJsActions.inputReset({ key }));
        } else {
            this.store.dispatch(quickJsActions.inputChanged({ key, value }));
        }
    }

    resetTip(tipOverrideKey: string): void {
        delete this.pendingTips[tipOverrideKey];
        this.store.dispatch(quickJsActions.inputReset({ key: tipOverrideKey }));
    }

    // ── Per-row Paid ─────────────────────────────────────────────────────────

    onPaidInput(key: string, value: number | null): void {
        this.pendingPaid[key] = value;
    }

    commitPaid(key: string): void {
        if (!(key in this.pendingPaid)) { return; }
        const value = this.pendingPaid[key];
        delete this.pendingPaid[key];
        if (value == null) {
            this.store.dispatch(quickJsActions.inputReset({ key }));
        } else {
            this.store.dispatch(quickJsActions.inputChanged({ key, value }));
        }
    }

    resetPaid(paidKey: string): void {
        delete this.pendingPaid[paidKey];
        this.store.dispatch(quickJsActions.inputReset({ key: paidKey }));
    }

    trackByUserId(_: number, row: { id: string }): string {
        return row.id;
    }

    trackByColKey(_: number, col: { key: string }): string {
        return col.key;
    }
}
